import React, { useEffect, useRef, useState } from 'react';

// Investment → Strategy → Profit icons — gold on navy for white background
const InvestmentGoalIcon = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Hand */}
    <path d="M6 28c0 0 3-2 8-2h5l3-1" stroke="#0B1F3B" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M7 32c2 1 5 2 9 2s9-2 12-4l3-3" stroke="#0B1F3B" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M20 26V18" stroke="#0B1F3B" strokeWidth="2" strokeLinecap="round"/>
    <path d="M20 22c-2-3-5-3-5-3s1 3 5 3z" fill="#D4A520"/>
    <path d="M20 20c2-3 5-3 5-3s-1 3-5 3z" fill="#D4A520"/>
    <circle cx="20" cy="13" r="6" fill="#D4A520"/>
    <text x="20" y="17" textAnchor="middle" fontSize="8" fill="#0B1F3B" fontWeight="bold">$</text>
  </svg>
);

const StrategyIcon = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Chess piece */}
    <rect x="12" y="32" width="16" height="4" rx="2" fill="#0B1F3B"/>
    <rect x="15" y="28" width="10" height="4" rx="1" fill="#0B1F3B"/>
    <ellipse cx="20" cy="24" rx="5" ry="5" fill="#0B1F3B"/>
    <circle cx="20" cy="17" r="4" fill="#0B1F3B"/>
    <circle cx="20" cy="14" r="2.5" fill="#D4A520"/>
    {/* Dollar coin */}
    <circle cx="30" cy="18" r="7" fill="#D4A520"/>
    <text x="30" y="22" textAnchor="middle" fontSize="9" fill="#0B1F3B" fontWeight="bold">$</text>
    {/* Arrow */}
    <path d="M28 8l6-4" stroke="#D4A520" strokeWidth="2.5" strokeLinecap="round"/>
    <path d="M32 4l2 0 0 2" stroke="#D4A520" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ProfitIcon = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Money bag */}
    <ellipse cx="20" cy="26" rx="13" ry="11" fill="#0B1F3B"/>
    <path d="M15 16c0 0 1-5 5-5s5 5 5 5" stroke="#0B1F3B" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M15 18c2-2 8-2 10 0" stroke="#D4A520" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <text x="20" y="30" textAnchor="middle" fontSize="12" fill="#D4A520" fontWeight="bold">$</text>
    {/* Growth arrow */}
    <path d="M28 12l6-6" stroke="#D4A520" strokeWidth="2.5" strokeLinecap="round"/>
    <path d="M32 6l2 0 0 2" stroke="#D4A520" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    {/* Bar chart mini */}
    <rect x="30" y="20" width="4" height="7" rx="1" fill="#D4A520"/>
    <rect x="35" y="16" width="4" height="11" rx="1" fill="#D4A520"/>
  </svg>
);

const STEPS = [
  {
    number: '01',
    IconComp: InvestmentGoalIcon,
    title: 'Join & Set Your Goals',
    description: 'Choose your membership tier, set your investment experience level, and define your financial goals. Our onboarding wizard personalizes your WIN experience.',
  },
  {
    number: '02',
    IconComp: StrategyIcon,
    title: 'Learn & Connect',
    description: 'Access WIN Academy courses, join community discussions, connect with mentors, and attend exclusive webinars with industry experts.',
  },
  {
    number: '03',
    IconComp: ProfitIcon,
    title: 'Invest & Grow',
    description: 'Track your portfolio, discover vetted investment opportunities, leverage AI insights, and build lasting wealth alongside thousands of women investors.',
  },
];

const HowItWorksSection = () => {
  const ref = useRef(null);
  const lineRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [lineWidth, setLineWidth] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          setTimeout(() => setLineWidth(100), 600);
        }
      },
      { threshold: 0.2 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="py-8 sm:py-14 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className={`text-center mb-6 sm:mb-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-flex items-center gap-2 bg-[#0B1F3B]/8 border border-[#0B1F3B]/15 rounded-full px-4 py-1.5 mb-4">
            <span className="text-[#0B1F3B] text-xs font-semibold tracking-widest uppercase">Your Journey</span>
          </div>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-[#0B1F3B] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Your Path to Financial Freedom
          </h2>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Connecting Line (desktop only) */}
          <div className="hidden md:block absolute top-16 left-[16.67%] right-[16.67%] h-0.5 bg-[#0B1F3B]/10">
            <div
              ref={lineRef}
              className="h-full bg-gradient-to-r from-[#D4A520] to-[#F0C830] transition-all duration-1500"
              style={{ width: `${lineWidth}%`, transitionDuration: '1.5s' }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {STEPS?.map((step, i) => (
              <div
                key={step?.number}
                className={`relative flex flex-col items-center text-center p-5 sm:p-8 rounded-2xl cursor-pointer ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{
                  transitionDelay: `${i * 200 + 300}ms`,
                  transition: 'opacity 700ms ease, transform 300ms ease-out, box-shadow 300ms ease-out',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(11,31,59,0.12), 0 4px 12px rgba(11,31,59,0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Vertical connector for mobile (between steps) */}
                {i < STEPS?.length - 1 && (
                  <div className="md:hidden absolute -bottom-3 left-1/2 -translate-x-1/2 w-0.5 h-6 bg-gradient-to-b from-[#D4A520] to-[#D4A520]/30" />
                )}
                {/* Step Number Circle */}
                <div className="relative z-10 w-16 h-16 rounded-full bg-[#D4A520] flex items-center justify-center shadow-lg shadow-[#D4A520]/30 mb-4">
                  <span className="text-[#0B1F3B] font-bold text-xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{step?.number}</span>
                </div>

                {/* Investment Icon */}
                <div className="mb-4 w-14 h-14 rounded-2xl bg-[#0B1F3B] flex items-center justify-center shadow-md">
                  <step.IconComp size={40} />
                </div>

                <h3 className="text-[#0B1F3B] font-bold text-xl mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {step?.title}
                </h3>
                <p className="text-[#0B1F3B]/60 text-sm leading-relaxed">{step?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
